/**
 * Task #19 — Customer-facing learner module view.
 *
 * Mirrors the staff TrainingModulePage but talks to /v1/learn/* (cookie
 * auth, no bearer) and resolves the module via the session cookie rather
 * than a URL param. Has no admin controls.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { LessonBlock, ModuleDetail, TrainingLesson } from '@xsc/shared';
import { ApiError } from '../lib/api-client.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { ScormBlockView } from '../components/training/scorm-block-view.js';
import { Button } from '../components/ui/button.js';
import { Badge } from '../components/ui/badge.js';

type LessonWithDone = TrainingLesson & { is_completed: boolean };

async function learnFetch(method: string, path: string, body?: unknown): Promise<unknown> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try { parsed = JSON.parse(text); } catch { parsed = text; }
  }
  if (!res.ok) throw new ApiError(res.status, parsed, `${method} ${path} failed`);
  return parsed;
}

function VideoBlock({
  block,
  onProgress,
}: {
  block: Extract<LessonBlock, { type: 'video' }>;
  onProgress?: (furthestSec: number, durationSec: number) => void;
}): JSX.Element {
  const furthestRef = useRef(0);
  const lastReportRef = useRef(0);
  if (block.provider !== 'mp4') {
    return (
      <figure className="my-3">
        {block.title && <figcaption className="font-semibold mb-2">{block.title}</figcaption>}
        <p className="text-sm text-ink-muted">
          (Embedded {block.provider} video — please watch the full video before marking complete.)
        </p>
      </figure>
    );
  }
  return (
    <figure className="my-3">
      {block.title && <figcaption className="font-semibold mb-2">{block.title}</figcaption>}
      <video
        src={block.url}
        controls
        preload="metadata"
        className="w-full rounded border border-border"
        onTimeUpdate={(e) => {
          const v = e.currentTarget;
          const cur = Math.floor(v.currentTime);
          if (cur > furthestRef.current) furthestRef.current = cur;
          const now = Date.now();
          if (now - lastReportRef.current < 5_000) return;
          if (!Number.isFinite(v.duration) || v.duration === 0) return;
          lastReportRef.current = now;
          onProgress?.(furthestRef.current, Math.floor(v.duration));
        }}
        onEnded={(e) => {
          const v = e.currentTarget;
          if (!Number.isFinite(v.duration) || v.duration === 0) return;
          onProgress?.(Math.floor(v.duration), Math.floor(v.duration));
        }}
      />
    </figure>
  );
}

function renderBlock(
  block: LessonBlock,
  key: number,
  onVideoProgress?: (f: number, d: number) => void,
): JSX.Element {
  switch (block.type) {
    case 'video':
      return <VideoBlock key={key} block={block} onProgress={onVideoProgress} />;
    case 'heading':
      return block.level === 3 ? (
        <h3 key={key} className="text-base font-semibold mt-4 mb-2">{block.text}</h3>
      ) : (
        <h2 key={key} className="text-lg font-semibold mt-5 mb-2">{block.text}</h2>
      );
    case 'paragraph':
      return (
        <div
          key={key}
          className="my-2 leading-relaxed [&_p]:my-2 [&_a]:underline [&_a]:text-accent
                     [&_ol]:list-decimal [&_ul]:list-disc [&_ol]:ml-6 [&_ul]:ml-6"
          dangerouslySetInnerHTML={{ __html: block.text }}
        />
      );
    case 'callout':
      return (
        <aside key={key} className="rounded border-l-4 px-4 py-3 my-3 border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700">
          <div className="font-semibold text-sm mb-2">{block.title}</div>
          <div className="space-y-1 text-sm">
            {block.items.map((line, i) => (
              <div key={i} dangerouslySetInnerHTML={{ __html: line }} />
            ))}
          </div>
        </aside>
      );
    case 'list': {
      const items = block.items.map((it, i) => <li key={i} className="my-1">{it}</li>);
      return block.ordered ? (
        <ol key={key} className="list-decimal ml-6 my-2">{items}</ol>
      ) : (
        <ul key={key} className="list-disc ml-6 my-2">{items}</ul>
      );
    }
    case 'table':
      return (
        <div key={key} className="my-3 overflow-x-auto">
          <table className="w-full text-sm border border-border">
            <thead className="bg-surface-alt">
              <tr>{block.headers.map((h, i) => <th key={i} className="border border-border px-2 py-1 text-left font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i}>{row.map((c, j) => <td key={j} className="border border-border px-2 py-1 align-top">{c}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'exercise':
      return (
        <aside key={key} className="rounded border border-border bg-surface-alt p-4 my-3">
          <div className="font-semibold mb-2">{block.title}</div>
          <p className="text-sm whitespace-pre-line">{block.instructions}</p>
        </aside>
      );
    case 'scorm':
      // External learners hit the runtime routes through their
      // learner-session cookie (`resolveLearnerOrStaff` accepts both
      // staff bearer auth and the learner cookie). The same iframe +
      // SCORM/xAPI shim works here as in the staff view.
      return (
        <ScormBlockView
          key={key}
          packageId={block.package_id}
          title={block.title}
        />
      );
    case 'divider':
      return <hr key={key} className="my-4 border-border" />;
  }
}

/**
 * Task #21 — when the renewal-reminder link lands here (?renewed=1) we
 * show a one-time banner explaining *why* the learner is back on this
 * page. The banner is purely informational and is dismissed on the
 * next navigation; the cert renewal itself happens when the learner
 * works through the module and re-passes the quiz.
 */
function RenewalBanner(): JSX.Element | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get('renewed') !== '1') return null;
  return (
    <aside className="rounded border-l-4 px-4 py-3 my-3 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700">
      <div className="font-semibold text-sm mb-1">Your certificate is expiring soon</div>
      <p className="text-sm">
        Re-complete this module and pass the quiz to refresh your
        certificate for another full term. Your previous certificate
        remains valid until its expiry date.
      </p>
    </aside>
  );
}

export function LearnerModulePage(): JSX.Element {
  const [detail, setDetail] = useState<ModuleDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [gateError, setGateError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    learnFetch('GET', '/v1/learn/module')
      .then((d) => setDetail(d as ModuleDetail))
      .catch((e: Error) => setErr(e.message));
  }, [tick]);

  if (err) return <p className="p-6 text-danger">Could not load your training. Your link may have expired.</p>;
  if (!detail) return <p className="p-6 text-ink-muted">Loading…</p>;

  const lessonList = detail.lessons as LessonWithDone[];
  if (lessonList.length === 0) {
    return <p className="p-6 text-ink-muted">No lessons yet.</p>;
  }
  const safeIdx = Math.min(activeIdx, lessonList.length - 1);
  const active = lessonList[safeIdx]!;
  const allDone = lessonList.every((l) => l.is_completed);

  const reportWatch = (lessonId: string, f: number, d: number) => {
    learnFetch('POST', `/v1/learn/lessons/${encodeURIComponent(lessonId)}/watch-progress`, {
      furthest_sec: f,
      duration_sec: d,
    }).catch(() => {});
  };

  const markComplete = async (lessonId: string) => {
    setGateError(null);
    try {
      await learnFetch('POST', '/v1/learn/progress', { lesson_id: lessonId });
      setTick((t) => t + 1);
      if (safeIdx < lessonList.length - 1) setActiveIdx(safeIdx + 1);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const body = e.body as {
          error?: string;
          pct?: number;
          score?: number | null;
          required?: number | null;
        } | null;
        if (body?.error === 'scorm_score_required') {
          const score = body.score !== null && body.score !== undefined
            ? `${Math.round(body.score)}%`
            : 'below the threshold';
          setGateError(
            `Your score (${score}) is below the required passing score of ${body.required ?? '—'}%. Retry the attempt in the lesson to try again.`,
          );
        } else if (body?.error === 'scorm_completion_required') {
          setGateError('The interactive package must report passed or completed before this lesson can be marked complete.');
        } else {
          const pct = body?.pct ?? 0;
          setGateError(`Please watch at least 90% of the video first (currently ${Math.round(pct * 100)}%).`);
        }
      } else {
        setGateError('Could not save progress. Please try again.');
      }
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-4">
      <RenewalBanner />
      <header>
        <h1 className="text-2xl font-semibold">{detail.module.title}</h1>
        {detail.module.description && (
          <p className="text-sm text-ink-muted">{detail.module.description}</p>
        )}
      </header>
      <div className="grid grid-cols-1 md:grid-cols-[16rem_1fr] gap-4">
        <Card>
          <CardHeader><CardTitle>Lessons</CardTitle></CardHeader>
          <CardBody className="p-0">
            <ol className="divide-y divide-border">
              {lessonList.map((l, i) => (
                <li key={l.id}>
                  <button
                    type="button"
                    onClick={() => setActiveIdx(i)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 hover:bg-surface ${i === safeIdx ? 'bg-surface' : ''}`}
                  >
                    <span aria-hidden className="mt-0.5 text-xs">
                      {l.is_completed ? '✓' : `${i + 1}.`}
                    </span>
                    <span className="flex-1">
                      <span className="block">{l.title}</span>
                      <span className="block text-xs text-ink-muted">
                        {l.estimated_minutes} min · {l.lesson_type}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{active.title}</CardTitle>
            {active.is_completed && <Badge tone="success">Completed</Badge>}
          </CardHeader>
          <CardBody>
            <article className="prose-sm max-w-none">
              {active.content.map((b, i) =>
                renderBlock(b, i, (f, d) => reportWatch(active.id, f, d)),
              )}
            </article>
            {gateError && <p className="text-sm text-warning mt-3">{gateError}</p>}
            <div className="mt-6 flex items-center justify-between gap-2 flex-wrap">
              <Button
                variant="secondary"
                disabled={safeIdx === 0}
                onClick={() => setActiveIdx((i) => Math.max(0, i - 1))}
              >Previous</Button>
              <div className="flex items-center gap-2">
                {!active.is_completed && !active.content.some((b) => b.type === 'scorm') && (
                  <Button onClick={() => markComplete(active.id)}>Mark complete</Button>
                )}
                {!active.is_completed && active.content.some((b) => b.type === 'scorm') && (
                  <span className="text-xs text-ink-muted italic">
                    Completion is recorded automatically when the interactive
                    package reports passed or completed.
                  </span>
                )}
                {safeIdx < lessonList.length - 1 ? (
                  <Button
                    variant="secondary"
                    onClick={() => setActiveIdx((i) => Math.min(lessonList.length - 1, i + 1))}
                  >Next</Button>
                ) : (
                  allDone && (
                    <Link to="/learn/quiz"><Button>Start quiz</Button></Link>
                  )
                )}
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
